import React, { useMemo, useState } from "react";
import {
    useGetTeamMembersQuery,
    useCreateTeamMemberMutation,
    useUpdateTeamMemberMutation,
    useDeleteTeamMemberMutation,
    useGetTeamDepartmentsQuery,
    useCreateTeamDepartmentMutation,
    useUpdateTeamDepartmentMutation,
    useDeleteTeamDepartmentMutation,
} from "../api";
import type {
    LocaleCode,
    TeamMemberDto,
    TeamMemberPayload,
    TeamDepartmentDto,
    TeamDepartmentPayload,
} from "../api/teamMembers";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

const SUPPORTED_LOCALES: LocaleCode[] = ["ru", "en", "uk", "ka"];

type FormState = TeamMemberPayload & {
    photoPreview?: string | null;
};

type DepartmentFormState = TeamDepartmentPayload & { id?: number };

type ApiErrorShape = {
    data?: {
        message?: string;
        errors?: Record<string, string[]>;
    };
    message?: string;
};

const emptyTranslations = () =>
    SUPPORTED_LOCALES.reduce(
        (acc, locale) => ({ ...acc, [locale]: "" }),
        {} as Record<string, string>
    );

const initialFormState = (): FormState => ({
    department_ids: [],
    name: emptyTranslations(),
    surname: emptyTranslations(),
    position: emptyTranslations(),
    country: emptyTranslations(),
    description: emptyTranslations(),
    order: 1,
    photo: null,
    photoPreview: null,
});

const initialDepartmentForm = (): DepartmentFormState => ({
    name: emptyTranslations(),
    color: "#3B82F6",
    order: 1,
});

const TeamMembersPage: React.FC = () => {
    const { data, isLoading, isFetching } = useGetTeamMembersQuery();
    const { data: departmentsData } = useGetTeamDepartmentsQuery();
    const [createMember, { isLoading: isCreating }] =
        useCreateTeamMemberMutation();
    const [updateMember, { isLoading: isUpdating }] =
        useUpdateTeamMemberMutation();
    const [deleteMember, { isLoading: isDeleting }] =
        useDeleteTeamMemberMutation();
    const [createDepartment, { isLoading: isCreatingDept }] =
        useCreateTeamDepartmentMutation();
    const [updateDepartment, { isLoading: isUpdatingDept }] =
        useUpdateTeamDepartmentMutation();
    const [deleteDepartment, { isLoading: isDeletingDept }] =
        useDeleteTeamDepartmentMutation();

    const { showError, showSuccess, notification, hideNotification } =
        useNotification();

    const [modalOpen, setModalOpen] = useState(false);
    const [deptModalOpen, setDeptModalOpen] = useState(false);
    const [activeLocale, setActiveLocale] = useState<LocaleCode>("ru");
    const [editingMember, setEditingMember] = useState<TeamMemberDto | null>(
        null
    );
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [editingDepartment, setEditingDepartment] =
        useState<TeamDepartmentDto | null>(null);
    const [departmentForm, setDepartmentForm] = useState<DepartmentFormState>(
        initialDepartmentForm
    );

    const departments = useMemo(
        () => departmentsData?.data ?? [],
        [departmentsData?.data]
    );

    const members = useMemo(
        () => data?.data ?? [],
        [data?.data]
    );

    const normalizeCountry = (country: TeamMemberDto["country"] | string): Record<string, string> => {
        if (country == null) return emptyTranslations();
        if (typeof country === "string") {
            const str = country;
            try {
                const parsed = JSON.parse(str) as Record<string, string>;
                if (parsed && typeof parsed === "object") {
                    return { ...emptyTranslations(), ...parsed };
                }
            } catch {
                // не JSON — один ISO-код для всех локалей
            }
            const code = str.trim().toLowerCase();
            return SUPPORTED_LOCALES.reduce(
                (acc, locale) => ({ ...acc, [locale]: code }),
                {} as Record<string, string>,
            );
        }
        if (typeof country === "object" && !Array.isArray(country)) {
            return { ...emptyTranslations(), ...country };
        }
        return emptyTranslations();
    };

    const handleOpenModal = (member?: TeamMemberDto) => {
        if (member) {
            setEditingMember(member);
            setFormState({
                department_ids: member.department_ids ?? [],
                name: { ...emptyTranslations(), ...member.name },
                surname: { ...emptyTranslations(), ...member.surname },
                position: { ...emptyTranslations(), ...member.position },
                country: normalizeCountry(member.country),
                description: { ...emptyTranslations(), ...(member.description ?? {}) },
                order: member.order,
                photo: null,
                photoPreview: member.photo_url ?? null,
            });
        } else {
            setEditingMember(null);
            setFormState(initialFormState);
        }
        setModalOpen(true);
    };

    const handleOpenDeptModal = (dept?: TeamDepartmentDto) => {
        if (dept) {
            setEditingDepartment(dept);
            setDepartmentForm({
                id: dept.id,
                name: { ...emptyTranslations(), ...dept.name },
                color: dept.color ?? "#3B82F6",
                order: dept.order ?? 1,
            });
        } else {
            setEditingDepartment(null);
            setDepartmentForm(initialDepartmentForm);
        }
        setDeptModalOpen(true);
    };

    const handleCloseDeptModal = () => {
        setDeptModalOpen(false);
        setEditingDepartment(null);
        setDepartmentForm(initialDepartmentForm);
    };

    const handleDepartmentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const payload: TeamDepartmentPayload = {
            name: departmentForm.name,
            color: departmentForm.color ?? null,
            order: departmentForm.order,
        };
        try {
            if (editingDepartment) {
                await updateDepartment({ id: editingDepartment.id, data: payload }).unwrap();
                showSuccess("Отдел обновлен.");
            } else {
                await createDepartment(payload).unwrap();
                showSuccess("Отдел создан.");
            }
            handleCloseDeptModal();
        } catch (err) {
            const e = err as ApiErrorShape;
            showError(
                e?.data?.errors
                    ? Object.values(e.data.errors).flat()[0]
                    : e?.data?.message || e?.message || "Ошибка сохранения отдела."
            );
        }
    };

    const handleDeleteDepartment = async (dept: TeamDepartmentDto) => {
        if (
            !window.confirm(
                `Удалить отдел "${dept.name.ru ?? dept.name.en}"? Сотрудники останутся без отдела.`
            )
        ) return;
        try {
            await deleteDepartment(dept.id).unwrap();
            showSuccess("Отдел удален.");
        } catch (err) {
            const e = err as ApiErrorShape;
            showError(e?.data?.message || e?.message || "Не удалось удалить отдел.");
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingMember(null);
        setFormState(initialFormState);
    };

    const handleTranslationChange = (
        field: "name" | "surname" | "position" | "country" | "description",
        locale: LocaleCode,
        value: string
    ) => {
        setFormState((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                [locale]: value,
            },
        }));
    };

    const handlePhotoChange = (file: File | null) => {
        if (!file) {
            setFormState((prev) => ({
                ...prev,
                photo: null,
                photoPreview: prev.photoPreview ?? null,
            }));
            return;
        }

        const preview = URL.createObjectURL(file);
        setFormState((prev) => ({
            ...prev,
            photo: file,
            photoPreview: preview,
        }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const payload: TeamMemberPayload = {
            department_ids: formState.department_ids && formState.department_ids.length > 0 ? formState.department_ids : undefined,
            name: formState.name,
            surname: formState.surname,
            position: formState.position,
            country: formState.country,
            description: formState.description,
            order: formState.order,
            photo: formState.photo ?? undefined,
        };

        try {
            if (editingMember) {
                await updateMember({ id: editingMember.id, data: payload }).unwrap();
                showSuccess("Данные участника обновлены.");
            } else {
                await createMember(payload).unwrap();
                showSuccess("Новый участник успешно добавлен.");
            }
            handleCloseModal();
        } catch (error) {
            const err = error as ApiErrorShape;
            const fieldError = err?.data?.errors
                ? Object.values(err.data.errors).flat()[0]
                : null;
            showError(
                fieldError ||
                    err?.data?.message ||
                    err?.message ||
                    "Не удалось сохранить изменения."
            );
        }
    };

    const handleDelete = async (member: TeamMemberDto) => {
        if (
            !window.confirm(
                `Удалить участника "${member.name.ru ?? member.name.en}"?`
            )
        ) {
            return;
        }

        try {
            await deleteMember(member.id).unwrap();
            showSuccess("Участник удален.");
        } catch (error) {
            const err = error as ApiErrorShape;
            const fieldError = err?.data?.errors
                ? Object.values(err.data.errors).flat()[0]
                : null;
            showError(
                fieldError ||
                    err?.data?.message ||
                    err?.message ||
                    "Не удалось удалить участника."
            );
        }
    };

    const renderLocaleTabs = () => (
        <div className="flex gap-2 mb-4">
            {SUPPORTED_LOCALES.map((locale) => (
                <button
                    key={locale}
                    type="button"
                    onClick={() => setActiveLocale(locale)}
                    className={`px-3 py-1 rounded ${
                        activeLocale === locale
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700"
                    }`}
                >
                    {locale.toUpperCase()}
                </button>
            ))}
        </div>
    );

    const isBusy = isCreating || isUpdating || isDeleting;
    const isDeptBusy = isCreatingDept || isUpdatingDept || isDeletingDept;

    return (
        <div className="p-6">
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold">Наша команда</h1>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => handleOpenDeptModal()}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-60"
                        disabled={isDeptBusy}
                    >
                        Отделы
                    </button>
                    <button
                        onClick={() => handleOpenModal()}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Добавить участника
                    </button>
                </div>
            </div>

            {/* Секция отделов */}
            {departments.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h2 className="text-lg font-medium mb-2">Отделы</h2>
                    <div className="flex flex-wrap gap-2">
                        {departments.map((d) => (
                            <span
                                key={d.id}
                                className="inline-flex items-center gap-2 px-3 py-1 rounded text-sm"
                                style={{
                                    backgroundColor: d.color ? `${d.color}20` : undefined,
                                    borderLeft: d.color ? `3px solid ${d.color}` : undefined,
                                }}
                            >
                                <span>{d.name.ru ?? d.name.en ?? "—"}</span>
                                <button
                                    type="button"
                                    className="text-gray-500 hover:text-blue-600"
                                    onClick={() => handleOpenDeptModal(d)}
                                >
                                    Изменить
                                </button>
                                <button
                                    type="button"
                                    className="text-gray-500 hover:text-red-600"
                                    onClick={() => handleDeleteDepartment(d)}
                                    disabled={isDeletingDept}
                                >
                                    Удалить
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {(isLoading || isFetching) && (
                <div className="text-gray-500">Загрузка...</div>
            )}

            <div className="overflow-x-auto bg-white shadow rounded">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                                #
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                                Имя (RU)
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                                Должность (RU)
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                                Фото
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-600">
                                Порядок
                            </th>
                            <th className="px-4 py-2 text-right text-sm font-semibold text-gray-600">
                                Действия
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {members.map((member) => (
                            <tr key={member.id}>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                    {member.id}
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                    {member.name.ru ||
                                        member.name.en ||
                                        "—"}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                    {member.position.ru ||
                                        member.position.en ||
                                        "—"}
                                </td>
                                <td className="px-4 py-2">
                                    {member.photo_url ? (
                                        <img
                                            src={member.photo_url}
                                            alt={member.name.ru || member.name.en}
                                            className="w-20 aspect-[16/9] rounded-md object-cover"
                                        />
                                    ) : (
                                        <span className="text-xs text-gray-400">
                                            нет
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                    {member.order}
                                </td>
                                <td className="px-4 py-2 text-right space-x-2">
                                    <button
                                        className="px-3 py-1 text-sm rounded bg-gray-100 hover:bg-gray-200"
                                        onClick={() => handleOpenModal(member)}
                                    >
                                        Редактировать
                                    </button>
                                    <button
                                        className="px-3 py-1 text-sm rounded bg-red-100 text-red-700 hover:bg-red-200"
                                        onClick={() => handleDelete(member)}
                                        disabled={isDeleting}
                                    >
                                        Удалить
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!members.length && !isLoading && (
                            <tr>
                                <td
                                    colSpan={6}
                                    className="px-4 py-6 text-center text-sm text-gray-500"
                                >
                                    Список пуст.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold">
                                {editingMember
                                    ? "Редактирование участника"
                                    : "Новый участник"}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        {renderLocaleTabs()}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <label className="flex flex-col text-sm text-gray-600">
                                    Имя ({activeLocale.toUpperCase()})
                                    <input
                                        type="text"
                                        className="mt-1 rounded border border-gray-300 px-3 py-2"
                                        value={
                                            formState.name[activeLocale] ?? ""
                                        }
                                        onChange={(e) =>
                                            handleTranslationChange(
                                                "name",
                                                activeLocale,
                                                e.target.value
                                            )
                                        }
                                        required={activeLocale === "ru"}
                                    />
                                </label>
                                <label className="flex flex-col text-sm text-gray-600">
                                    Фамилия ({activeLocale.toUpperCase()})
                                    <input
                                        type="text"
                                        className="mt-1 rounded border border-gray-300 px-3 py-2"
                                        value={
                                            formState.surname[activeLocale] ?? ""
                                        }
                                        onChange={(e) =>
                                            handleTranslationChange(
                                                "surname",
                                                activeLocale,
                                                e.target.value
                                            )
                                        }
                                        required={activeLocale === "ru"}
                                    />
                                </label>
                            </div>

                            <label className="flex flex-col text-sm text-gray-600">
                                Должность ({activeLocale.toUpperCase()})
                                <input
                                    type="text"
                                    className="mt-1 rounded border border-gray-300 px-3 py-2"
                                    value={
                                        formState.position[activeLocale] ?? ""
                                    }
                                    onChange={(e) =>
                                        handleTranslationChange(
                                            "position",
                                            activeLocale,
                                            e.target.value
                                        )
                                    }
                                    required={activeLocale === "ru"}
                                />
                                {activeLocale !== "ru" && (
                                    <span className="mt-1 text-xs text-gray-500">
                                        Если не заполнено, будет использовано значение RU.
                                    </span>
                                )}
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Страна ({activeLocale.toUpperCase()})
                                <input
                                    type="text"
                                    className="mt-1 rounded border border-gray-300 px-3 py-2"
                                    value={formState.country?.[activeLocale] ?? ""}
                                    onChange={(e) =>
                                        handleTranslationChange(
                                            "country",
                                            activeLocale,
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Описание ({activeLocale.toUpperCase()})
                                <textarea
                                    className="mt-1 rounded border border-gray-300 px-3 py-2 min-h-[96px]"
                                    value={formState.description?.[activeLocale] ?? ""}
                                    onChange={(e) =>
                                        handleTranslationChange(
                                            "description",
                                            activeLocale,
                                            e.target.value
                                        )
                                    }
                                />
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Отделы
                                <div className="mt-1 space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded p-2">
                                    {departments.length === 0 ? (
                                        <span className="text-xs text-gray-500">Нет доступных отделов</span>
                                    ) : (
                                        departments.map((dept) => (
                                            <label key={dept.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={formState.department_ids?.includes(dept.id) ?? false}
                                                    onChange={(e) => {
                                                        const currentIds = formState.department_ids ?? [];
                                                        if (e.target.checked) {
                                                            setFormState((prev) => ({
                                                                ...prev,
                                                                department_ids: [...currentIds, dept.id],
                                                            }));
                                                        } else {
                                                            setFormState((prev) => ({
                                                                ...prev,
                                                                department_ids: currentIds.filter((id) => id !== dept.id),
                                                            }));
                                                        }
                                                    }}
                                                    className="rounded"
                                                />
                                                <span
                                                    className="text-sm"
                                                    style={{
                                                        color: dept.color ?? undefined,
                                                    }}
                                                >
                                                    {dept.name.ru ?? dept.name.en ?? `Отдел #${dept.id}`}
                                                </span>
                                            </label>
                                        ))
                                    )}
                                </div>
                                <span className="mt-1 text-xs text-gray-500">
                                    Выберите один или несколько отделов для сотрудника.
                                </span>
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Порядок отображения
                                <input
                                    type="number"
                                    min={1}
                                    className="mt-1 rounded border border-gray-300 px-3 py-2"
                                    value={formState.order ?? 1}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            order: Number(e.target.value),
                                        }))
                                    }
                                />
                                <span className="mt-1 text-xs text-gray-500">
                                    При сохранении записи с этим порядком остальные записи с таким же или большим
                                    порядком будут сдвинуты.
                                </span>
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Фото
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="mt-1"
                                    onChange={(e) =>
                                        handlePhotoChange(
                                            e.target.files?.[0] ?? null
                                        )
                                    }
                                />
                            </label>

                            {formState.photoPreview && (
                                <img
                                    src={formState.photoPreview}
                                    alt="preview"
                                    className="w-full max-w-xs aspect-[16/9] rounded-md object-cover"
                                />
                            )}

                            <div className="flex items-center justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded border border-gray-300 text-gray-600"
                                    onClick={handleCloseModal}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                    disabled={isBusy}
                                >
                                    {editingMember ? "Сохранить" : "Добавить"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deptModalOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold">
                                {editingDepartment
                                    ? "Редактирование отдела"
                                    : "Новый отдел"}
                            </h2>
                            <button
                                onClick={handleCloseDeptModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        {renderLocaleTabs()}

                        <form onSubmit={handleDepartmentSubmit} className="space-y-4">
                            <label className="flex flex-col text-sm text-gray-600">
                                Название отдела ({activeLocale.toUpperCase()})
                                <input
                                    type="text"
                                    className="mt-1 rounded border border-gray-300 px-3 py-2"
                                    value={
                                        departmentForm.name[activeLocale] ?? ""
                                    }
                                    onChange={(e) =>
                                        setDepartmentForm((prev) => ({
                                            ...prev,
                                            name: {
                                                ...prev.name,
                                                [activeLocale]: e.target.value,
                                            },
                                        }))
                                    }
                                    required={activeLocale === "ru"}
                                />
                                {activeLocale !== "ru" && (
                                    <span className="mt-1 text-xs text-gray-500">
                                        Если не заполнено, будет использовано значение RU.
                                    </span>
                                )}
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Порядок отображения
                                <input
                                    type="number"
                                    min={1}
                                    className="mt-1 rounded border border-gray-300 px-3 py-2"
                                    value={departmentForm.order ?? 1}
                                    onChange={(e) =>
                                        setDepartmentForm((prev) => ({
                                            ...prev,
                                            order: Number(e.target.value),
                                        }))
                                    }
                                />
                                <span className="mt-1 text-xs text-gray-500">
                                    Порядок сортировки отделов. Чем меньше число, тем выше отдел в списке.
                                </span>
                            </label>

                            <label className="flex flex-col text-sm text-gray-600">
                                Цвет (HEX)
                                <div className="flex items-center gap-2 mt-1">
                                    <input
                                        type="color"
                                        className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                                        value={departmentForm.color ?? "#3B82F6"}
                                        onChange={(e) =>
                                            setDepartmentForm((prev) => ({
                                                ...prev,
                                                color: e.target.value,
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="flex-1 rounded border border-gray-300 px-3 py-2 font-mono text-sm"
                                        value={departmentForm.color ?? "#3B82F6"}
                                        onChange={(e) =>
                                            setDepartmentForm((prev) => ({
                                                ...prev,
                                                color: e.target.value,
                                            }))
                                        }
                                        placeholder="#3B82F6"
                                        pattern="^#[0-9A-Fa-f]{6}$"
                                    />
                                </div>
                                <span className="mt-1 text-xs text-gray-500">
                                    Цвет для выделения отдела на фронтенде.
                                </span>
                            </label>

                            <div className="flex items-center justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded border border-gray-300 text-gray-600"
                                    onClick={handleCloseDeptModal}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                    disabled={isDeptBusy}
                                >
                                    {editingDepartment ? "Сохранить" : "Создать"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamMembersPage;

